/**
 * Operational warehouses (work orders, transfers, adjustments): enabled Megaventory
 * sites except in-transit / SGST staging. MRP planning sites are a subset.
 */
import { useQuery } from "@tanstack/react-query";

import { apiFetch, type ApiError } from "@/lib/api";
import { normalizedOperationalLocationCode } from "@integrator-src/location/operational-location-code";

export interface TransferEndpointLocationRow {
  id: number;
  name: string | null;
  abbreviation: string | null;
  mrp_site_code: string | null;
  megaventory_id?: number | null;
}

export function transferEndpointLocationLabel(
  location: TransferEndpointLocationRow | undefined,
  fallbackId: number,
): string {
  if (location == null) return `Location ${String(fallbackId)}`;
  const abbreviation = location.abbreviation?.trim() ?? "";
  if (abbreviation !== "") return abbreviation;
  const name = location.name?.trim() ?? "";
  if (name !== "") return name;
  return `Location ${String(fallbackId)}`;
}

export function operationalLocationSelectLabel(location: TransferEndpointLocationRow): string {
  const name = location.name?.trim() ?? "";
  const abbreviation = location.abbreviation?.trim() ?? "";
  if (name !== "" && abbreviation !== "" && name.toLowerCase() !== abbreviation.toLowerCase()) {
    return `${name} (${abbreviation})`;
  }
  return name || abbreviation || `Location ${String(location.id)}`;
}

export function transferEndpointLocationLabelById(
  locations: readonly TransferEndpointLocationRow[],
  locationId: number,
): string {
  const match = locations.find((location) => location.id === locationId);
  return transferEndpointLocationLabel(match, locationId);
}

export function operationalLocationSelectRank(location: TransferEndpointLocationRow): number {
  const code = normalizedOperationalLocationCode({
    publicLocationId: location.id,
    mrpSiteCode: location.mrp_site_code,
    abbreviation: location.abbreviation,
  });
  return ({ kodiak: 0, sgs: 1, suntronic: 2 } as Record<string, number>)[code] ?? 100;
}

export function compareTransferEndpointLocations(
  left: TransferEndpointLocationRow,
  right: TransferEndpointLocationRow,
): number {
  const rank = operationalLocationSelectRank(left) - operationalLocationSelectRank(right);
  return rank !== 0
    ? rank
    : transferEndpointLocationLabel(left, left.id).localeCompare(
        transferEndpointLocationLabel(right, right.id),
      );
}

export function sortTransferEndpointLocations(
  locations: readonly TransferEndpointLocationRow[],
): TransferEndpointLocationRow[] {
  return [...locations].sort(compareTransferEndpointLocations);
}

export function partitionTransferEndpointLocations(
  locations: readonly TransferEndpointLocationRow[],
): { primary: TransferEndpointLocationRow[]; other: TransferEndpointLocationRow[] } {
  const sorted = sortTransferEndpointLocations(locations);
  return {
    primary: sorted.filter((location) => operationalLocationSelectRank(location) < 100),
    other: sorted.filter((location) => operationalLocationSelectRank(location) >= 100),
  };
}

export const OPERATIONAL_LOCATION_NATIVE_SELECT_DIVIDER_VALUE = "__operational_location_divider__";

function useOperationalLocationList(path: "/api/manufacturing/operational-locations" | "/api/mrp/transfer-locations"): {
  locations: TransferEndpointLocationRow[];
  isLoading: boolean;
  error: ApiError | null;
} {
  const query = useQuery<{ locations: TransferEndpointLocationRow[] }, ApiError>({
    queryKey: ["operational-locations", path],
    queryFn: () => apiFetch<{ locations: TransferEndpointLocationRow[] }>(path),
    staleTime: 5 * 60_000,
  });
  return {
    locations: query.data?.locations ?? [],
    isLoading: query.isLoading,
    error: query.error ?? null,
  };
}

/** Manufacturing family pages (work orders, CM purchase orders). */
export function useOperationalLocations(): {
  locations: TransferEndpointLocationRow[];
  isLoading: boolean;
  error: ApiError | null;
} {
  return useOperationalLocationList("/api/manufacturing/operational-locations");
}

/** MRP-module pages (transfers, transmute adjustments). */
export function useTransferEndpointLocations(): {
  locations: TransferEndpointLocationRow[];
  isLoading: boolean;
  error: ApiError | null;
} {
  return useOperationalLocationList("/api/mrp/transfer-locations");
}
